import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const pageMinimum = 1
const itemMinimum = 15

const expectedItems = [15, 30, 45]

export interface usePaginationResult {
  currentPage: number
  setPage: (page: number) => void
  itemsPerPage: '15' | '30' | '45'
  setItemsPerPage: (perPage: number) => void
}

type PropTypes = {
  initialPage?: number
  initialItemsPerPage?: number
  queryParamPrefix?: string
}

const usePagination = ({ initialPage = pageMinimum, initialItemsPerPage = itemMinimum, queryParamPrefix = '' }: PropTypes): usePaginationResult => {
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)

  const [currentPage, setCurrentPage] = useState(
    Math.max(pageMinimum, searchParams.has(`${queryParamPrefix}Page`) ? parseInt(searchParams.get(`${queryParamPrefix}Page`)!, 10) : initialPage),
  )
  const [itemsPerPage, setCurrentItemsPerPage] = useState(
    Math.max(itemMinimum, searchParams.has(`${queryParamPrefix}PerPage`) ? parseInt(searchParams.get(`${queryParamPrefix}PerPage`)!, 10) : initialItemsPerPage),
  )

  useEffect(() => {
    // Update URL with the current page
    const updatedSearchParams = new URLSearchParams(window.location.search)
    updatedSearchParams.set(`${queryParamPrefix}Page`, String(currentPage))
    updatedSearchParams.set(`${queryParamPrefix}PerPage`, String(itemsPerPage))

    // Replace the current state in the history without adding a new entry
    window.history.replaceState(null, '', `${window.location.pathname}?${updatedSearchParams.toString()}`)
  }, [currentPage, itemsPerPage, queryParamPrefix])

  // Reset the items per page if unexpected value
  useEffect(() => {
    if (!expectedItems.includes(itemsPerPage)) {
      setItemsPerPage(itemMinimum)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPerPage])

  const setPage = (page: number) => {
    // Ensure the minimum pageNum is default
    const newPage = Math.max(pageMinimum, page)
    setCurrentPage(newPage)
  }

  const setItemsPerPage = (perPage: number) => {
    // Reset page on items per row change
    setPage(pageMinimum)
    // Ensure the minimum itemsPerPage is default
    const newPerPage = Math.max(itemMinimum, perPage)
    setCurrentItemsPerPage(newPerPage)
  }

  return {
    currentPage,
    setPage,
    itemsPerPage: itemsPerPage.toString() as usePaginationResult['itemsPerPage'],
    setItemsPerPage,
  }
}

export default usePagination
